import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";
import { workspaceRailClass } from "./workspace-rail";
import { NAV_ROW_ACTIVE, NAV_ROW_IDLE } from "./nav-row";

export interface WorkspaceFrameProps {
  /** The strip, and anything that sits directly under it on a narrow screen. */
  header: ReactNode;
  /** The rail, or nothing on a screen that has no navigation. */
  rail?: ReactNode;
  /** Fixed layers drawn over the page: a tab bar, an open drawer. */
  overlay?: ReactNode;
  mainClassName?: string;
  /** Classes merged into the row that holds the rail and the page, for a minimum height the default does not give it. */
  bodyClassName?: string;
  /** Classes merged into the page's measure, for a cap the default does not give it. */
  pageClassName?: string;
  style?: CSSProperties;
  children: ReactNode;
}

/**
 * The workspace both shells share: the ambient ground, a floating glass strip
 * and rail on a wide screen, and the page running flush beside them at one
 * measure.
 */
export function WorkspaceFrame({
  header,
  rail,
  overlay,
  mainClassName,
  bodyClassName,
  pageClassName,
  style,
  children,
}: WorkspaceFrameProps) {
  return (
    // flow-root keeps the strip's top margin inside this box: collapsed through it,
    // the box (and its ambient light) started 8px down, leaving a bare band on top.
    <div className="flow-root min-h-dvh bg-ground workspace-ground" style={style}>
      {header}
      <div className={cn("flex min-h-[calc(100dvh-var(--header-h))]", bodyClassName)}>
        {rail}
        <main className={cn("min-w-0 grow px-4 py-6 lg:px-8 lg:py-7", mainClassName)}>
          <div className={cn("mx-auto w-full max-w-[76.8rem]", pageClassName)}>{children}</div>
        </main>
      </div>
      {overlay}
    </div>
  );
}

export interface WorkspaceHeaderProps extends HTMLAttributes<HTMLElement> {
  /**
   * Room for the rail beside the strip. `expanded` and `collapsed` are for a
   * frame that decides in script whether the rail is showing; `wide` leaves the
   * room at the `lg` breakpoint, for a rail that is simply hidden below it.
   */
  railOffset?: "none" | "expanded" | "collapsed" | "wide";
}

/** The glass strip across the top: 52px, floating off the edges on a wide screen. */
export function WorkspaceHeader({
  railOffset = "none",
  className,
  ...props
}: WorkspaceHeaderProps) {
  return (
    <header
      className={cn(
        "sticky top-0 z-20 flex flex-col border-b workspace-chrome rail-motion lg:top-2 lg:mt-2 lg:mr-2 lg:rounded-[var(--radius-panel)] lg:border",
        railOffset === "none" && "lg:ml-2",
        railOffset === "expanded" && "ml-55",
        railOffset === "collapsed" && "ml-17",
        railOffset === "wide" && "lg:ml-55",
        className,
      )}
      {...props}
    />
  );
}

export interface WorkspaceRailProps extends HTMLAttributes<HTMLElement> {
  collapsed?: boolean;
}

/** The glass rail: full height, floating beside the strip on a wide screen. */
export function WorkspaceRail({ collapsed = false, className, ...props }: WorkspaceRailProps) {
  return (
    <nav
      data-rail={collapsed ? "closed" : "open"}
      className={cn(workspaceRailClass(collapsed), className)}
      {...props}
    />
  );
}

/** One destination in a rail. */
export interface RailNavItem {
  id: string;
  label: ReactNode;
  active?: boolean;
  /** Present and unreachable, with the reason said on the row. */
  disabled?: boolean;
  /** A short word at the row's far edge — a count, or why it is off. */
  note?: ReactNode;
}

/** A labelled run of destinations. An unlabelled group sits above the rest. */
export interface RailNavGroup {
  label?: string;
  items: RailNavItem[];
}

export interface RailNavProps {
  groups: RailNavGroup[];
  /**
   * Draws one row as the app's own link. The design system knows nothing
   * about a router, so the app hands its link over with the row's classes.
   */
  renderLink: (
    item: RailNavItem,
    props: { className: string; children: ReactNode },
  ) => ReactNode;
  className?: string;
}

const RAIL_ROW = "flex items-center gap-2.5 rounded-sm px-3 py-1.5 text-ui nav-motion";

/** A rail's destinations, drawn from a list of screens, in groups. */
export function RailNav({ groups, renderLink, className }: RailNavProps) {
  return (
    <div className={cn("flex flex-col gap-3.5", className)}>
      {groups.map((group, index) =>
        group.items.length === 0 ? null : (
          <div key={group.label ?? `group-${index}`} className="flex flex-col gap-px">
            {group.label ? (
              <p className="px-3 pt-0.5 pb-1.5 font-mono text-meta tracking-[0.08em] text-ink-3 uppercase">
                {group.label}
              </p>
            ) : null}
            {group.items.map((item) => {
              const body = (
                <>
                  <span className="min-w-0 truncate">{item.label}</span>
                  {item.note ? (
                    <span className="ml-auto pl-2 font-mono text-meta text-ink-3 tabular-nums">
                      {item.note}
                    </span>
                  ) : null}
                </>
              );
              if (item.disabled) {
                return (
                  <span
                    key={item.id}
                    aria-disabled="true"
                    className={cn(RAIL_ROW, "text-ink-3")}
                  >
                    {body}
                  </span>
                );
              }
              return (
                <span key={item.id} className="contents">
                  {renderLink(item, {
                    className: cn(RAIL_ROW, item.active ? NAV_ROW_ACTIVE : NAV_ROW_IDLE),
                    children: body,
                  })}
                </span>
              );
            })}
          </div>
        ),
      )}
    </div>
  );
}
